import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import callsService from '../../services/callsService';

// Async thunks for API calls
export const fetchCalls = createAsyncThunk(
  'calls/fetchCalls',
  async ({ limit = 10, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const data = await callsService.list({ limit, offset });
      const raw = data?.data;
      return Array.isArray(raw) ? raw : (raw?.calls ?? []);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCallStats = createAsyncThunk(
  'calls/fetchCallStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await callsService.stats();
      const raw = data?.data;
      const summary = raw?.summary ?? raw;
      if (!summary || typeof summary !== 'object') return null;
      const totalCalls = Number(summary.total_calls ?? summary.totalCalls ?? 0) || 0;
      const totalDuration = Number(summary.total_duration ?? summary.totalDuration ?? 0) || 0;
      const completedCalls = Number(summary.completed_calls ?? summary.completedCalls ?? 0) || 0;
      const successRate = totalCalls > 0 ? Math.round((completedCalls / totalCalls) * 100) : 0;
      return {
        totalCalls,
        totalDuration,
        avgDuration: Number(summary.avg_duration ?? summary.avgDuration ?? 0) || 0,
        successRate,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const startCall = createAsyncThunk(
  'calls/startCall',
  async (callData, { rejectWithValue }) => {
    try {
      const data = await callsService.start(callData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const endCall = createAsyncThunk('calls/endCall', async (callId, { rejectWithValue }) => {
  try {
    const data = await callsService.end(callId);
    return data.data;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const deleteCall = createAsyncThunk(
  'calls/deleteCall',
  async (callId, { rejectWithValue }) => {
    try {
      await callsService.delete(callId);
      return callId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Call History
  calls: [],
  callsLoading: false,
  callsError: null,

  // Current Call
  currentCall: null,

  // Call Statistics
  callStats: {
    totalCalls: 0,
    totalDuration: 0,
    avgDuration: 0,
    successRate: 0,
  },
  statsLoading: false,
  statsError: null,

  // Audio/Recording State
  isRecording: false,
  transcript: [],
  isInterrupted: false,

  // Socket Connection
  socket: null,
  isConnected: false,
  isSocketReady: false,
};

const callsSlice = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    // Socket Management
    setSocket: (state, action) => {
      state.socket = action.payload;
    },
    setConnectionStatus: (state, action) => {
      state.isConnected = action.payload;
    },
    setSocketReady: (state, action) => {
      state.isSocketReady = action.payload;
    },

    // Current Call Management
    setCurrentCall: (state, action) => {
      state.currentCall = action.payload;
    },

    // Recording Management
    setRecording: (state, action) => {
      state.isRecording = action.payload;
    },
    setInterrupted: (state, action) => {
      state.isInterrupted = action.payload;
    },

    // Transcript Management
    addTranscriptMessage: (state, action) => {
      state.transcript.push(action.payload);
      // Clear interrupted state when AI responds
      if (action.payload.type === 'ai') {
        state.isInterrupted = false;
      }
    },
    clearTranscript: state => {
      state.transcript = [];
    },

    // Error Management
    clearError: state => {
      state.callsError = null;
      state.statsError = null;
    },

    // Add call (for real-time updates)
    addCall: (state, action) => {
      state.calls.unshift(action.payload);
    },

    // Update call (for real-time updates)
    updateCall: (state, action) => {
      const index = state.calls.findIndex(call => call._id === action.payload._id);
      if (index !== -1) {
        state.calls[index] = action.payload;
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch calls
      .addCase(fetchCalls.pending, state => {
        state.callsLoading = true;
        state.callsError = null;
      })
      .addCase(fetchCalls.fulfilled, (state, action) => {
        state.callsLoading = false;
        const payload = action.payload;
        state.calls = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.calls)
            ? payload.calls
            : [];
      })
      .addCase(fetchCalls.rejected, (state, action) => {
        state.callsLoading = false;
        state.callsError = action.payload;
      })
      // Fetch call stats
      .addCase(fetchCallStats.pending, state => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchCallStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.callStats = action.payload || state.callStats;
      })
      .addCase(fetchCallStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      })
      // Start call
      .addCase(startCall.pending, state => {
        state.callsLoading = true;
        state.callsError = null;
      })
      .addCase(startCall.fulfilled, (state, action) => {
        state.callsLoading = false;
        state.currentCall = action.payload;
        state.calls.unshift(action.payload);
      })
      .addCase(startCall.rejected, (state, action) => {
        state.callsLoading = false;
        state.callsError = action.payload;
      })
      // End call
      .addCase(endCall.pending, state => {
        state.callsLoading = true;
        state.callsError = null;
      })
      .addCase(endCall.fulfilled, (state, action) => {
        state.callsLoading = false;
        state.currentCall = null;
        // Update the call in the list
        const index = state.calls.findIndex(call => call._id === action.payload._id);
        if (index !== -1) {
          state.calls[index] = action.payload;
        }
      })
      .addCase(endCall.rejected, (state, action) => {
        state.callsLoading = false;
        state.callsError = action.payload;
      })
      // Delete call
      .addCase(deleteCall.pending, state => {
        state.callsLoading = true;
        state.callsError = null;
      })
      .addCase(deleteCall.fulfilled, (state, action) => {
        state.callsLoading = false;
        state.calls = state.calls.filter(call => call._id !== action.payload);
      })
      .addCase(deleteCall.rejected, (state, action) => {
        state.callsLoading = false;
        state.callsError = action.payload;
      });
  },
});

export const {
  setSocket,
  setConnectionStatus,
  setSocketReady,
  setCurrentCall,
  setRecording,
  setInterrupted,
  addTranscriptMessage,
  clearTranscript,
  clearError,
  addCall,
  updateCall,
} = callsSlice.actions;

// Selectors
export const selectCalls = state => state.calls.calls;
export const selectCurrentCall = state => state.calls.currentCall;
export const selectCallStats = state => state.calls.callStats;
export const selectIsRecording = state => state.calls.isRecording;
export const selectTranscript = state => state.calls.transcript;
export const selectIsInterrupted = state => state.calls.isInterrupted;
export const selectSocket = state => state.calls.socket;
export const selectIsConnected = state => state.calls.isConnected;
export const selectIsSocketReady = state => state.calls.isSocketReady;

// Loading selectors
export const selectCallsLoading = state => state.calls.callsLoading;
export const selectStatsLoading = state => state.calls.statsLoading;

// Error selectors
export const selectCallsError = state => state.calls.callsError;
export const selectStatsError = state => state.calls.statsError;

export default callsSlice.reducer;
